// The atlas cells of the sea mine marks (v2-the-deep.md §11.8, R2652).
//
// The cards of round 069 that hold still cost "a cheap batch of 16 atlas
// cells": four kinds, two states and two sides. This module is the one
// spelling of the key of such a cell and the one reader of it, the rule
// `combatCellKey` and `combatSpecFor` follow. `fast/gfx/atlas.ts` asks it,
// so the client lane that draws sea objects on the map writes a key and
// gets the cell.
//
// The key of one sea mine is
// `sea:m:<look>:<kind>:<armed><hostile>:<reading>:<contact>:<splash>`.
//
// `<armed>` and `<hostile>` are 0 or 1. The reading matters to the AUTO look
// alone, and the contact radius and the splash to card 09 alone; the key
// carries them always, so one spelling serves every look.
//
// A cell is baked WHITE, and the layer tints the quad with the side ink. The
// look draws at t = 0 with the motion off: a look that moves
// (`MOVING_SEA_MINE_STYLES`) draws each frame from `seamine-marks.ts`, or
// takes this still cell where the player turned the motion off.
//
// The generic mine mark of a heard row (R2902) is one more cell, key
// `sea:g:<r>:<spike>:<spikes>:<width>` in tenths of a map px, baked white
// as the others are.
//
// This is a leaf (`leaves.test.ts`). It draws on a plain 2D context.

import type { AtlasSpec } from '../fast/gfx/atlas';
import { SEA_MINE_STYLES } from '../gen/balance';
import { SEA_INK } from './seaobject-kit';
import {
  SEA_MINE_KINDS, genericMineExtent, paintGenericMine, paintSeaMine, seaMineExtent,
  type GenericMineFigures, type SeaChartReading, type SeaMineKind, type SeaMineStyle,
} from './seamine-marks';

/** The supersampling of a sea mine cell: the hulls' own. */
const SEA_MINE_CELL_SCALE = 4;

const READINGS: readonly SeaChartReading[] = ['historical', 'modern', 'off'];

/** The figures one cell of a kind is drawn to. */
export interface SeaMineCellFigures {
  contact: number;
  splash: number;
}

/** The key of one sea mine cell. */
export function seaMineCellKey(
  style: SeaMineStyle, kind: SeaMineKind, armed: boolean, hostile: boolean, reading: SeaChartReading,
  f: SeaMineCellFigures,
): string {
  return `sea:m:${style}:${kind}:${armed ? 1 : 0}${hostile ? 1 : 0}:${reading}:${Math.round(f.contact)}:${Math.round(f.splash)}`;
}

/** The key of the generic mine mark cell (R2902). */
export function genericMineCellKey(f: GenericMineFigures): string {
  const t = (v: number) => Math.round(v * 10);
  return `sea:g:${t(f.r)}:${t(f.spike)}:${Math.round(f.spikes)}:${t(f.width)}`;
}

/** The cell of a generic mine mark key, or null for a key that is not one. */
function genericMineSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p.length !== 6) return null;
  const n = p.slice(2).map(Number);
  if (!n.every((v) => Number.isFinite(v) && v >= 0) || !(n[0] > 0) || !(n[3] > 0)) return null;
  const f: GenericMineFigures = { r: n[0] / 10, spike: n[1] / 10, spikes: n[2], width: n[3] / 10 };
  const half = genericMineExtent(f);
  return {
    key,
    scale: SEA_MINE_CELL_SCALE,
    box: { x: -half, y: -half, w: 2 * half, h: 2 * half },
    draw(ctx) { paintGenericMine(ctx, SEA_INK.white, f); },
  };
}

/** The cell of a key, or null for a key this module does not own. */
export function seaObjectSpecFor(key: string): AtlasSpec | null {
  if (key.startsWith('sea:g:')) return genericMineSpecFor(key);
  if (!key.startsWith('sea:m:')) return null;
  const p = key.split(':');
  if (p.length !== 8) return null;
  const style = p[2] as SeaMineStyle, kind = p[3] as SeaMineKind, flags = p[4], reading = p[5] as SeaChartReading;
  const contact = Number(p[6]), splash = Number(p[7]);
  if (!(SEA_MINE_STYLES as readonly string[]).includes(style) || !SEA_MINE_KINDS.includes(kind)) return null;
  if (!/^[01][01]$/.test(flags) || !READINGS.includes(reading)) return null;
  if (!(contact > 0) || !(splash >= 0)) return null;
  const half = seaMineExtent(style, kind, contact, splash);
  return {
    key,
    scale: SEA_MINE_CELL_SCALE,
    box: { x: -half, y: -half, w: 2 * half, h: 2 * half },
    draw(ctx) {
      paintSeaMine(ctx, style, kind, {
        col: SEA_INK.white, armed: flags[0] === '1', hostile: flags[1] === '1',
        t: 0, ph: 0.5, s: 1, da: 0, motion: false, contact, splash, reading,
      });
    },
  };
}
